import { Scroll } from '@/lib/scroll'
import { onPageIn, onMount, onDestroy } from '@/modules/_'

export default function (element: HTMLElement, dataset: DOMStringMap): void {
  // Type definitions
  interface ImageData {
    element: HTMLImageElement
    aspectRatio: number
    index: number
    container: HTMLElement
  }

  interface GridRow {
    images: ImageData[]
    colSpans: number[]
    rowIndex: number
  }

  // Smart Sequential Row Layout
  async function applySmartSequentialLayout(
    containerSelector: string,
    totalColumns: number = 12
  ): Promise<void> {
    const container: HTMLElement | null =
      document.querySelector(containerSelector)

    if (!container) {
      throw new Error(`Container not found: ${containerSelector}`)
    }

    // Mobile fallback
    if (window.innerWidth < 750) {
      applyMobileLayout(container)
      return
    }

    // Get all images and load them
    const images: HTMLImageElement[] = Array.from(
      container.querySelectorAll('img')
    )
    const imageData: ImageData[] = []

    // Load all images in parallel
    await Promise.all(images.map((img) => waitForImageLoad(img)))

    // Process each image
    for (let i = 0; i < images.length; i++) {
      const img = images[i]

      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        continue
      }

      const aspectRatio = img.naturalWidth / img.naturalHeight
      const imageContainer = findImageContainer(img)

      imageData.push({
        element: img,
        aspectRatio,
        index: i,
        container: imageContainer,
      })
    }

    // Process images sequentially and create rows
    const rows = createRowsSequentially(imageData, totalColumns)

    // Apply the layout
    applyGridLayout(container, rows, totalColumns)
  }

  // Create rows by processing images sequentially
  function createRowsSequentially(
    images: ImageData[],
    totalColumns: number
  ): GridRow[] {
    const rows: GridRow[] = []
    const used: boolean[] = new Array(images.length).fill(false)

    for (let i = 0; i < images.length; i++) {
      if (used[i]) continue

      const currentImage = images[i]

      // Check if this image should take full width (wide images)
      if (currentImage.aspectRatio > 2.0) {
        rows.push({
          images: [currentImage],
          colSpans: [totalColumns],
          rowIndex: rows.length + 1,
        })
        used[i] = true
        continue
      }

      // Determine how many columns this image should take
      const baseColSpan = getColumnSpan(currentImage.aspectRatio, totalColumns)

      // Start building a row
      const rowImages = [currentImage]
      const rowColSpans = [baseColSpan]
      let usedCols = baseColSpan
      used[i] = true

      // Try to find 1 compatible image (max 2 images per row for better sizing)
      for (let j = i + 1; j < images.length; j++) {
        if (used[j]) continue

        const candidate = images[j]
        const candidateColSpan = getColumnSpan(
          candidate.aspectRatio,
          totalColumns
        )

        // Check if it fits and is compatible
        if (
          usedCols + candidateColSpan <= totalColumns &&
          areAspectRatiosCompatible(
            currentImage.aspectRatio,
            candidate.aspectRatio
          )
        ) {
          rowImages.push(candidate)
          rowColSpans.push(candidateColSpan)
          usedCols += candidateColSpan
          used[j] = true
          break // Only take 1 partner for better sizing
        }
      }

      // Adjust column spans to fill the row
      const adjustedSpans = adjustToFillRow(rowColSpans, totalColumns)

      rows.push({
        images: rowImages,
        colSpans: adjustedSpans,
        rowIndex: rows.length + 1,
      })
    }

    return rows
  }

  // Get column span based on aspect ratio - more generous sizing
  function getColumnSpan(aspectRatio: number, totalColumns: number): number {
    // Wide landscape
    if (aspectRatio > 1.6) return 8 // About 2/3 width

    // Regular landscape
    if (aspectRatio > 1.2) return 6 // Half width

    // Square-ish
    if (aspectRatio > 0.9) return 5 // Bit less than half

    // Portrait
    return 4 // 1/3 width
  }

  // Check compatibility - stricter rules
  function areAspectRatiosCompatible(ratio1: number, ratio2: number): boolean {
    const diff = Math.abs(ratio1 - ratio2)

    // Landscapes with landscapes
    if (ratio1 > 1.1 && ratio2 > 1.1) {
      return diff < 0.4
    }

    // Squares with squares
    if (ratio1 > 0.8 && ratio1 < 1.3 && ratio2 > 0.8 && ratio2 < 1.3) {
      return diff < 0.3
    }

    // Portraits with portraits
    if (ratio1 < 1.0 && ratio2 < 1.0) {
      return diff < 0.2
    }

    return false
  }

  // Adjust spans to fill the row completely
  function adjustToFillRow(spans: number[], totalColumns: number): number[] {
    const currentTotal = spans.reduce((sum, span) => sum + span, 0)

    if (currentTotal >= totalColumns) {
      return spans
    }

    const adjusted = [...spans]
    const remaining = totalColumns - currentTotal

    // Distribute remaining columns proportionally
    for (let i = 0; i < remaining; i++) {
      const index = i % adjusted.length
      adjusted[index]++
    }

    return adjusted
  }

  // Apply the grid layout - let CSS Grid handle row heights naturally
  function applyGridLayout(
    container: HTMLElement,
    rows: GridRow[],
    totalColumns: number
  ): void {
    container.style.display = 'grid'
    container.style.gridTemplateColumns = `repeat(${totalColumns}, 1fr)`
    container.style.gap = '1rem'
    container.style.gridAutoRows = 'auto'

    rows.forEach((row) => {
      let colStart = 1

      row.images.forEach((imageData, index) => {
        const colSpan = row.colSpans[index]
        const container = imageData.container
        const img = imageData.element

        // Set grid position
        container.style.gridColumn = `${colStart} / span ${colSpan}`
        container.style.gridRow = `${row.rowIndex}`

        // Let container size naturally - no fixed height
        container.style.height = 'auto'

        // Image fills its container naturally
        img.style.width = '100%'
        img.style.height = 'auto'
        img.style.display = 'block'

        colStart += colSpan
      })
    })
  }

  // Mobile layout fallback
  function applyMobileLayout(container: HTMLElement): void {
    container.style.display = 'grid'
    container.style.gridTemplateColumns = '1fr'
    container.style.gap = '10px'
    container.style.gridAutoRows = 'auto'

    Array.from(container.children).forEach((item: Element) => {
      const htmlItem = item as HTMLElement
      htmlItem.style.gridColumn = ''
      htmlItem.style.gridRow = ''
      htmlItem.style.height = 'auto'
    })
  }

  // Helper function to find the appropriate container for an image
  function findImageContainer(img: HTMLImageElement): HTMLElement {
    const galleryItem = img.closest('[data-gallery-item]')
    if (galleryItem) {
      return galleryItem as HTMLElement
    }

    const parentElement = img.parentElement
    if (parentElement) {
      return parentElement
    }

    throw new Error('No suitable container found for image')
  }

  // Helper function to ensure image is loaded
  function waitForImageLoad(img: HTMLImageElement): Promise<void> {
    return new Promise<void>((resolve) => {
      if (img.complete && img.naturalHeight !== 0) {
        resolve()
      } else {
        const onLoad = () => {
          img.removeEventListener('load', onLoad)
          img.removeEventListener('error', onError)
          resolve()
        }

        const onError = () => {
          img.removeEventListener('load', onLoad)
          img.removeEventListener('error', onError)
          resolve()
        }

        img.addEventListener('load', onLoad)
        img.addEventListener('error', onError)
      }
    })
  }

  function setTextColor(): void {
    const header = document.querySelector('[data-header]') as HTMLElement
    const headerText = document.querySelector(
      '[data-header-text]'
    ) as HTMLElement

    if (!header || !headerText) return

    // Get computed background color
    const bgColor = getComputedStyle(header).backgroundColor

    // If transparent or no background, use fallback values for brightness calculation
    const rgb =
      bgColor.includes('rgba(0, 0, 0, 0)') || bgColor === 'transparent'
        ? [252, 252, 243] // Your fallback #FCFCF3 as RGB
        : bgColor.match(/\d+/g)?.map(Number) || [252, 252, 243]

    // Calculate brightness
    const brightness = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000

    // Set text color
    headerText.style.color = brightness > 128 ? '#111112' : '#fcfcf3'
  }

  function setHeadingClass(): void {
    const header = document.querySelector('[data-title]') as HTMLElement
    if (!header) return

    const words = header.textContent?.split(' ') || []
    const hasLongWord = words.some((word) => word.length > 8)

    if (hasLongWord) {
      header.classList.remove('u-text-style-h1')
      header.classList.add('u-text-style-h2')
    }
  }

  onMount(async () => {
    const scrollPosition = window.scrollY // Save scroll position

    setTextColor()
    setHeadingClass()

    await applySmartSequentialLayout('.gallery', 12).catch((error) => {
      console.error('Error applying layout:', error)
    })

    window.scrollTo(0, scrollPosition) // Restore scroll position
  })

  onDestroy(() => {})
}
